export * from './metal-base';

export enum UserRole {
  ADMIN = 'ADMIN',
  ESTIMATOR = 'ESTIMATOR',
  VIEWER = 'VIEWER',
}

export enum ProjectStatus {
  NEW = 'NEW',
  IN_PROGRESS = 'IN_PROGRESS',
  REVIEW = 'REVIEW',
  COMPLETED = 'COMPLETED',
  ON_HOLD = 'ON_HOLD',
}

export enum ProjectType {
  RESIDENTIAL = 'RESIDENTIAL',
  COMMERCIAL = 'COMMERCIAL',
  INDUSTRIAL = 'INDUSTRIAL',
  MIXED_USE = 'MIXED_USE',
  RETAIL = 'RETAIL',
  HEALTHCARE = 'HEALTHCARE',
  EDUCATION = 'EDUCATION',
}

export enum CompanyType {
  CONTRACTOR = 'CONTRACTOR',
  MANUFACTURER = 'MANUFACTURER',
  ARCHITECT = 'ARCHITECT',
  DESIGNER = 'DESIGNER',
  GENERAL_CONTRACTOR = 'GENERAL_CONTRACTOR',
  SUBCONTRACTOR = 'SUBCONTRACTOR',
  SUPPLIER = 'SUPPLIER',
  OTHER = 'OTHER',
}

export enum KnowledgeItemType {
  EMAIL = 'EMAIL',
  PDF = 'PDF',
  IMAGE = 'IMAGE',
  LINK = 'LINK',
  DRAWING = 'DRAWING',
}

export enum KnowledgeSource {
  MANUAL_UPLOAD = 'MANUAL_UPLOAD',
  OPENCLAW_AGENT = 'OPENCLAW_AGENT',
}

export enum ProcessingStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export enum FolderType {
  BID_DOCUMENTS = 'BID_DOCUMENTS',
  ADDITIONAL_SCOPE_CHANGE_ORDERS = 'ADDITIONAL_SCOPE_CHANGE_ORDERS',
  RFIS = 'RFIS',
  REDLINES = 'REDLINES',
  FIELD_DIMENSIONS = 'FIELD_DIMENSIONS',
  APPROVED_DRAWINGS = 'APPROVED_DRAWINGS',
  SHOP_DRAWINGS = 'SHOP_DRAWINGS',
  CONTRACT_DOCS = 'CONTRACT_DOCS',
  CREDIT_MEMOS = 'CREDIT_MEMOS',
  ESTIMATES = 'ESTIMATES',
  INVOICES = 'INVOICES',
  MATERIALS = 'MATERIALS',
  PACKAGE_SLIPS = 'PACKAGE_SLIPS',
  PLANS = 'PLANS',
  PURCHASE_ORDERS = 'PURCHASE_ORDERS',
  CHANGE_ORDERS = 'CHANGE_ORDERS',
}

export enum DrawingStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  IN_REVIEW = 'IN_REVIEW',
  APPROVED = 'APPROVED',
  REVISE = 'REVISE',
}

export enum MarkupCategory {
  AREA = 'AREA',
  DIMENSION = 'DIMENSION',
  MATERIAL_CALLOUT = 'MATERIAL_CALLOUT',
  NOTE = 'NOTE',
}

export enum MarkupPurpose {
  MEASUREMENT = 'MEASUREMENT',
  REVIEW_COMMENT = 'REVIEW_COMMENT',
  CHANGE_REQUEST = 'CHANGE_REQUEST',
}

export enum AiExtractionStatus {
  PENDING = 'PENDING',
  APPLIED = 'APPLIED',
  OVERRIDDEN = 'OVERRIDDEN',
  REJECTED = 'REJECTED',
}

export enum UnclassifiedEmailStatus {
  PENDING = 'PENDING',
  CLASSIFIED = 'CLASSIFIED',
  DISCARDED = 'DISCARDED',
}

export enum MarkupContext {
  FLOOR_PLAN = 'FLOOR_PLAN',
  SECTION_CUT = 'SECTION_CUT',
  DETAIL = 'DETAIL',
  ELEVATION = 'ELEVATION',
}

// ═══════════════════════════════════════════════════════════════
// Cabinet Estimator Enums (ported from Cabinet_Estimator)
// ═══════════════════════════════════════════════════════════════

export enum CabinetType {
  BASE_CABINET = 'Base Cabinet',
  UPPER_CABINET = 'Upper Cabinet',
  TALL_CABINET = 'Tall Cabinet',
  VANITY = 'Vanity',
  DRAWER_BANK = 'Drawer Bank',
  OPEN_SHELF = 'Open Shelf',
  CORNER = 'Corner',
  APPLIANCE_GARAGE = 'Appliance Garage',
  WINE_RACK = 'Wine Rack',
  FILLER = 'Filler',
  PANEL = 'Panel',
  ISLAND = 'Island',
  BASE = 'Base',
}

export enum ConstructionType {
  FRAMELESS = 'Frameless',
  FACE_FRAME = 'Face Frame',
  HYBRID = 'Hybrid',
}

export enum TopStyle {
  STRETCHERS = 'stretchers',
  FULL_PANEL = 'full_panel',
  NONE = 'none',
}

export enum ToeKickStyle {
  DETACHED = 'detached',
  INTEGRATED = 'integrated',
  NONE = 'none',
}

export enum GrainDirection {
  NONE = 'none',
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
  LENGTHWISE = 'lengthwise',
}

export enum MaterialCategory {
  SHEET_GOOD = 'Sheet Good',
  SOLID_WOOD = 'Solid Wood',
  EDGEBAND = 'Edgeband',
  DOOR = 'Door',
  DRAWER_BOX = 'Drawer Box',
  COUNTERTOP = 'Countertop',
  OTHER = 'Other',
}

export enum MaterialUnit {
  SHEET = 'sheet',
  LINEAR_FOOT = 'linear_foot',
  SQUARE_FOOT = 'square_foot',
  EACH = 'each',
  ROLL = 'roll',
  YARD = 'yard',
  /**
   * Bulk stock bought by weight — blown fiberfill is the first case. It has no
   * stock rectangle and reports no area, so it is priced from
   * MaterialUnitPricingOptions.weightLb and never from sheet_width /
   * sheet_length. Adding a member here is HALF the change: without a matching
   * UNIT_ALIASES entry in utils/material-unit.ts the value normalises to null
   * and is priced per square foot.
   */
  POUND = 'pound',
}

/**
 * Which set of fields the Add/Edit Material modal shows for a material type.
 *
 * Visibility used to be a hardcoded name set in MaterialsLibraryPage, so a
 * type created through the UI always got the full panel form. Storing it on
 * the type lets a new type declare its shape at creation time.
 *
 * PANEL is the default because it is what every type rendered as before this
 * existed — an un-migrated row therefore behaves exactly as it did.
 */
export enum MaterialTypeFormProfile {
  PANEL = 'panel',
  COATING = 'coating',
  FABRIC = 'fabric',
}

export enum FabricSubtype {
  FABRIC = 'Fabric',
  VINYL = 'Vinyl',
  VELVET = 'Velvet',
  MOHAIR = 'Mohair',
}

export enum PatternDirection {
  NONE = 'none',
  RAILROAD = 'railroad',
  UP_THE_BOLT = 'up_the_bolt',
}

export enum HardwareUnit {
  EACH = 'each',
  PAIR = 'pair',
  SET = 'set',
  SQ_FT = 'sq ft',
  FT = 'ft',
  YARDS = 'yards',
  LINEAR_FOOT = 'linear_foot',
}

export enum HardwareCategory {
  HINGES = 'Hinges',
  DRAWER_SLIDES = 'Drawer Slides',
  PULLS = 'Pulls',
  KNOBS = 'Knobs',
  SHELF_SUPPORTS = 'Shelf Supports',
  CATCHES = 'Catches',
  DAMPERS = 'Dampers',
  LEGS = 'Legs',
  FASTENERS = 'Fasteners',
  UPHOLSTERY = 'Upholstery',
}

export enum LaborPricingMode {
  FIXED = 'fixed',
  PER_HOUR = 'per_hour',
}

export enum LaborUnit {
  PER_CABINET = 'per cabinet',
  PER_DOOR = 'per door',
  PER_DRAWER = 'per drawer',
  PER_SHELF = 'per shelf',
  PER_HINGE = 'per hinge',
  PER_PULL_KNOB = 'per pull/knob',
  PER_PANEL = 'per panel',
  PER_ZONE = 'per zone',
  PER_LEAF_ZONE = 'per leaf zone',
  PER_CUBBY_CELL = 'per cubby cell',
  PER_SQ_FT = 'per sq ft',
  PER_TRIP = 'per trip',
  PER_HOUR = 'per hour',
  PER_LINEAR_FOOT = 'per linear foot',
  PER_SECTION = 'per section',
  PER_RIB = 'per rib',
  PER_RAIL = 'per rail',
  PER_SLOT = 'per slot',
}

export enum LaborCategory {
  ASSEMBLY = 'Assembly',
  FINISHING = 'Finishing',
  DELIVERY = 'Delivery',
  INSTALLATION = 'Installation',
  COUNTERTOP = 'Countertop',
  OTHER = 'Other',
}

export enum ComplexityTier {
  ANY = 'any',
  STANDARD = 'standard',
  MODERATE = 'moderate',
  COMPLEX = 'complex',
}

export enum FinishFaceFilter {
  ALL = 'all',
  DOORS_ONLY = 'doors_only',
  EXTERIOR_ONLY = 'exterior_only',
  DOORS_AND_EXTERIOR = 'doors_and_exterior',
}

export enum QuantityRule {
  FIXED = 'fixed',
  PER_DOOR = 'per_door',
  PER_DRAWER = 'per_drawer',
  PER_SHELF = 'per_shelf',
  PER_LINEAR_FOOT = 'per_linear_foot',
  FORMULA = 'formula',
}

export enum MirrorMode {
  NONE = 'none',
  X = 'x',
  Y = 'y',
  Z = 'z',
}

export enum MountSurface {
  DOOR_PANEL = 'Door Panel',
  DRAWER_FRONT = 'Drawer Front',
  LEFT_PANEL = 'Left Panel',
  RIGHT_PANEL = 'Right Panel',
  BOTTOM_PANEL = 'Bottom Panel',
  TOP_PANEL = 'Top Panel',
  BACK_PANEL = 'Back Panel',
  SHELF = 'Shelf',
  STRETCHER = 'Stretcher',
}

export enum MountFace {
  FRONT = 'front',
  BACK = 'back',
  TOP = 'top',
  BOTTOM = 'bottom',
  LEFT = 'left',
  RIGHT = 'right',
}

export enum AlignOption {
  LEFT = 'left',
  RIGHT = 'right',
  TOP = 'top',
  BOTTOM = 'bottom',
  MIDDLE = 'middle',
  FRONT = 'front',
  BACK = 'back',
}

export enum ZoneBehavior {
  NONE = 'none',
  ZONE_SHELF = 'zone_shelf',
  VERTICAL_DIVIDER = 'vertical_divider',
  HORIZONTAL_DIVIDER = 'horizontal_divider',
}

export enum PartCategory {
  STRUCTURAL = 'Structural',
  FACE_FRAME = 'Face Frame',
  FACE_COMPONENT = 'Face Component',
}

export enum AssemblyUnitType {
  LINEAR_FOOT = 'LF',
  SQUARE_FOOT = 'SF',
  EACH = 'EA',
}

export enum EstimateProjectStatus {
  DRAFT = 'Draft',
  ACTIVE = 'Active',
  COMPLETED = 'Completed',
  ARCHIVED = 'Archived',
}

export enum CustomChargeCategory {
  MISCELLANEOUS = 'Miscellaneous',
  DELIVERY = 'Delivery',
  INSTALLATION = 'Installation',
  DESIGN = 'Design',
  ENGINEERING = 'Engineering',
}

export enum DrillPatternUnit {
  MM = 'mm',
  INCHES = 'inches',
}

export enum ModelFormat {
  GLB = 'glb',
  GLTF = 'gltf',
  OBJ = 'obj',
}

export enum EventStatus {
  DRAFT = 'draft',
  SCHEDULED = 'scheduled',
  CONFIRMED = 'confirmed',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  POSTPONED = 'postponed',
}

export enum EventVisibility {
  PRIVATE = 'private',
  TEAM = 'team',
  PUBLIC = 'public',
}

export enum RelatedEntity {
  PROJECT = 'project',
  CLIENT = 'client',
  VENDOR = 'vendor',
  EMPLOYEE = 'employee',
}

export enum AccountType {
  DOMAIN = 'DOMAIN',
  INVITED = 'INVITED',
}

export enum PermissionAction {
  READ = 'READ',
  WRITE = 'WRITE',
  DELETE = 'DELETE',
}

export enum CncJobStatus {
  INCOMING_CUTS = 'INCOMING_CUTS',
  PENDING = 'PENDING',
  TODAY_CUT_LIST = 'TODAY_CUT_LIST',
  EXPRESS_CUT = 'EXPRESS_CUT',
  IN_PROCESS = 'IN_PROCESS',
  PENDING_TO_SUBTRACT = 'PENDING_TO_SUBTRACT',
  IN_SHELVES = 'IN_SHELVES',
}

export enum AppModule {
  ESTIMATION = 'ESTIMATION',
  MANUFACTURING = 'MANUFACTURING',
  LOGISTICS = 'LOGISTICS',
  INVENTORY = 'INVENTORY',
}

export enum ViewportType {
  SECTION_CUT = 'section_cut',
  ELEVATION = 'elevation',
  DETAIL = 'detail',
  PLAN = 'plan',
}

export enum Currency {
  USD = 'USD',
  MXN = 'MXN',
}

export enum Department {
  MANUFACTURING = 'MANUFACTURING',
  ENGINEERING = 'ENGINEERING',
  INVENTORY = 'INVENTORY',
  LOGISTICS = 'LOGISTICS',
  SOFTWARE_ENGINEERING = 'SOFTWARE_ENGINEERING',
  INSTALLATIONS = 'INSTALLATIONS',
  ESTIMATING = 'ESTIMATING',
}

export enum RoleLevel {
  MANAGER = 'manager',
  ASSOCIATE = 'associate',
}

export const DEPARTMENT_LABELS: Record<Department, string> = {
  [Department.MANUFACTURING]: 'Manufacturing',
  [Department.ENGINEERING]: 'Engineering',
  [Department.INVENTORY]: 'Inventory',
  [Department.LOGISTICS]: 'Logistics',
  [Department.SOFTWARE_ENGINEERING]: 'Software Engineering',
  [Department.INSTALLATIONS]: 'Installations',
  [Department.ESTIMATING]: 'Estimating',
};

export enum ThreadSubjectType {
  EVENT = 'event',
}

export enum NotificationType {
  MENTION = 'mention',
}
